/**
 * The customer an order is linked to and their phone, as the server sends
 * them with every order, and the rule for offering that phone on the M-Pesa
 * prompt at checkout. Kept free of React so it can be tested.
 */
import { normalizeSafaricomPhone, safaricomNumberValidation } from './libs';

export interface OrderCustomerContact {
  customerId: string | null;
  customerPhone: string | null;
}

/** The two fields the server puts on an order document. */
export const orderCustomerContactFromDoc = (
  order:
    | {
        bp_order_customer_id?: string | null;
        bp_order_customer_phone_number?: string | null;
      }
    | null
    | undefined,
): OrderCustomerContact => ({
  customerId: order?.bp_order_customer_id
    ? String(order.bp_order_customer_id)
    : null,
  customerPhone:
    String(order?.bp_order_customer_phone_number || '').trim() || null,
});

/**
 * The number as the M-Pesa field holds it ("+254712345678"), or null when it
 * is not a Safaricom number the prompt could reach.
 */
export const offerableMpesaPhone = (
  raw: string | null | undefined,
): string | null => {
  const local = normalizeSafaricomPhone(String(raw || ''));
  if (!safaricomNumberValidation(local)) return null;
  return `+254${local.slice(1)}`;
};

/** "0712 345 678", "+254712345678" and "254712345678" are one number. */
export const sameMpesaNumber = (a: string, b: string): boolean => {
  const left = normalizeSafaricomPhone(a);
  const right = normalizeSafaricomPhone(b);
  return left.length > 0 && left === right;
};

export type MpesaPrefillDecision =
  | { action: 'apply'; phone: string }
  | { action: 'clear' }
  | { action: 'keep' };

export interface MpesaPrefillInput {
  customerId: string | null;
  customerPhone: string | null;
  /** What the field holds now. */
  fieldPhone: string;
  /** The number the prefill last put in the field, if any. */
  appliedPhone: string | null;
  /** The cashier changed the offered number for this customer. */
  touched: boolean;
  /** Customer whose number the cashier dismissed with "Use another". */
  dismissedCustomerId: string | null;
  /** A number restored from a saved checkout; it wins over the customer's. */
  restoredPhone: string;
}

const KEEP: MpesaPrefillDecision = { action: 'keep' };
const CLEAR: MpesaPrefillDecision = { action: 'clear' };

/**
 * Whether the M-Pesa field should take the customer's number. The cashier's
 * own typing always wins; the offered number follows the customer only while
 * the field still holds it.
 */
export const decideMpesaPrefill = (
  input: MpesaPrefillInput,
): MpesaPrefillDecision => {
  const field = input.fieldPhone.trim();
  const heldByPrefill =
    Boolean(input.appliedPhone) &&
    field.length > 0 &&
    sameMpesaNumber(field, input.appliedPhone as string);
  if (field && !heldByPrefill) return KEEP;
  if (input.touched) return KEEP;
  if (!field && input.restoredPhone.trim()) return KEEP;

  const offered = offerableMpesaPhone(input.customerPhone);
  const dismissed =
    Boolean(input.customerId) && input.customerId === input.dismissedCustomerId;
  if (!offered || dismissed) return heldByPrefill ? CLEAR : KEEP;
  if (heldByPrefill && sameMpesaNumber(field, offered)) return KEEP;
  return { action: 'apply', phone: offered };
};
